import { assertSessionResponseCurrent, inheritSessionResponseGuard } from '@/session/fetch';
import { isSessionTransition } from '@/session/operation';

export type FileDownloadFromResponseResult = {
  success: boolean;
  canceled?: true;
  filename?: string;
  contentType?: string;
  error?: string;
};

type FileDownloadFromResponseOptions = {
  canPublish?: () => boolean;
};

const canceledDownload = (): FileDownloadFromResponseResult => ({ success: false, canceled: true });

const canPublishDownload = (response: Response, options: FileDownloadFromResponseOptions): boolean => {
  assertSessionResponseCurrent(response);
  return options.canPublish?.() ?? true;
};

// Check if browser supports streaming downloads
export const supportsStreaming = (): boolean => {
  return (
    typeof ReadableStream !== 'undefined' &&
    typeof Response !== 'undefined' &&
    // typeof Response.prototype.body !== 'undefined' &&
    typeof window.URL !== 'undefined' &&
    typeof window.URL.createObjectURL !== 'undefined'
  );
};

// Extract filename from content-disposition header
const extractFilenameFromHeaders = (contentDisposition: string | null, fallbackFilename: string): string => {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  // Try UTF-8 filename first
  const utf8FilenameMatch = contentDisposition.match(/filename\*=\s*([^;]+)/);
  if (utf8FilenameMatch && utf8FilenameMatch[1]) {
    try {
      const [charset, , encodedFilename] = utf8FilenameMatch[1].split("'");
      if (charset.toLowerCase() === 'utf-8') {
        return decodeURIComponent(encodedFilename);
      }
    } catch (e) {
      // Fallback to ASCII filename if UTF-8 decoding fails
    }
  }

  // Try ASCII filename
  const asciiFilenameMatch = contentDisposition.match(/filename="([^"]+)"/);
  if (asciiFilenameMatch && asciiFilenameMatch[1]) {
    return asciiFilenameMatch[1];
  }

  // finally just see if it's filename=somefile.csv
  const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1];
  }
  return fallbackFilename;
};

// Process final filename based on content type
const processFinalFilename = (filename: string, contentType: string): string => {
  if (contentType.includes('zip') && !filename.toLowerCase().endsWith('.zip')) {
    return filename + '.zip';
  }
  return filename;
};

// Trigger file download in browser
const triggerDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};

// Process response headers and extract metadata
const processResponseHeaders = (response: Response, fallbackFilename: string) => {
  const contentType = response.headers.get('content-type') || '';
  const contentDisposition = response.headers.get('content-disposition');
  const filename = extractFilenameFromHeaders(contentDisposition, fallbackFilename);
  const finalFilename = processFinalFilename(filename, contentType);

  return { contentType, finalFilename };
};

// Streaming download implementation
const fileDownloadFromResponseStream = async (
  response: Response,
  fallbackFilename: string,
  options: FileDownloadFromResponseOptions
): Promise<FileDownloadFromResponseResult> => {
  try {
    const { contentType, finalFilename } = processResponseHeaders(response, fallbackFilename);

    // Use streaming approach with ReadableStream
    if (!response.body) {
      throw new Error('Response body is not available for streaming');
    }

    const reader = response.body.getReader();
    const stream = new ReadableStream({
      start(controller) {
        function pump(): Promise<void> {
          assertSessionResponseCurrent(response);
          return reader.read().then(({ done, value }) => {
            assertSessionResponseCurrent(response);
            if (done) {
              controller.close();
              return;
            }
            controller.enqueue(value);
            return pump();
          });
        }
        return pump();
      },
    });

    // Create a new response from the stream to create a blob
    const streamResponse = new Response(stream, {
      headers: response.headers,
    });

    const blob = await streamResponse.blob();
    if (!canPublishDownload(response, options)) return canceledDownload();
    triggerDownload(blob, finalFilename);

    return { success: true, filename: finalFilename, contentType, error: undefined };
  } catch (e) {
    if (isSessionTransition(e) || options.canPublish?.() === false) return canceledDownload();
    console.error('Error with streaming download:', e);
    throw e; // Re-throw to allow fallback
  }
};

// Original blob-based download (fallback)
const fileDownloadFromResponseBlob = async (
  response: Response,
  fallbackFilename: string,
  options: FileDownloadFromResponseOptions
): Promise<FileDownloadFromResponseResult> => {
  try {
    const { contentType, finalFilename } = processResponseHeaders(response, fallbackFilename);

    // Note: This approach loads the entire response into memory, which is not true streaming.
    // For large files, this could cause memory issues. A streaming approach would use:
    // const reader = response.body.getReader();
    // However, browser security restrictions and cross-browser compatibility make
    // the current blob approach more reliable for most use cases.
    const blob = await response.blob();
    if (!canPublishDownload(response, options)) return canceledDownload();
    triggerDownload(blob, finalFilename);

    return { success: true, filename: finalFilename, contentType, error: undefined };
  } catch (e) {
    if (isSessionTransition(e) || options.canPublish?.() === false) return canceledDownload();
    console.error('Error downloading file:', e);
    const message = e instanceof Error ? e.message : String(e);
    return { success: false, error: message, filename: undefined, contentType: undefined };
  }
};

// Main export function with streaming support and fallback
export const fileDownloadFromResponse = async (
  response: Response,
  fallbackFilename: string,
  options: FileDownloadFromResponseOptions = {}
): Promise<FileDownloadFromResponseResult> => {
  // Check if streaming is supported and try streaming first
  if (supportsStreaming()) {
    // Clone the response before attempting streaming to allow fallback
    let responseClone: Response;
    try {
      responseClone = inheritSessionResponseGuard(response, response.clone());
    } catch (error) {
      if (isSessionTransition(error) || options.canPublish?.() === false) return canceledDownload();
      console.warn('Cannot clone response, falling back to blob approach:', error);
      return await fileDownloadFromResponseBlob(response, fallbackFilename, options);
    }

    try {
      return await fileDownloadFromResponseStream(response, fallbackFilename, options);
    } catch (error) {
      if (isSessionTransition(error) || options.canPublish?.() === false) return canceledDownload();
      console.warn('Streaming download failed, falling back to blob approach:', error);

      // Use the cloned response for fallback
      try {
        return await fileDownloadFromResponseBlob(responseClone, fallbackFilename, options);
      } catch (fallbackError) {
        if (isSessionTransition(fallbackError) || options.canPublish?.() === false) return canceledDownload();
        console.error('Fallback to blob approach also failed:', fallbackError);
        const message = error instanceof Error ? error.message : String(error);
        return { success: false, error: `Both streaming and blob approaches failed: ${message}`, filename: undefined, contentType: undefined };
      }
    }
  }

  // Fall back to blob approach (original method) for browsers that don't support streaming
  return await fileDownloadFromResponseBlob(response, fallbackFilename, options);
};

/**
 * Download utility with streaming support and fallback
 *
 * This utility automatically detects browser streaming capabilities and:
 * - Uses ReadableStream approach for better memory efficiency on large files
 * - Falls back to blob approach for older browsers or when streaming fails
 * - Provides consistent error handling and filename detection
 *
 * Browser Support:
 * - Modern browsers (Chrome 65+, Firefox 65+, Safari 10.1+): Uses streaming
 * - Older browsers: Uses blob approach
 *
 * Usage:
 * ```typescript
 * const result = await fileDownloadFromResponse(response, 'fallback.csv');
 * if (result.success) {
 *   console.log('Downloaded:', result.filename);
 * }
 * ```
 */
